import { useQuery } from "@tanstack/react-query"
import { AlertCircle, ArrowLeft } from "lucide-react"
import { Link, useParams } from "react-router"
import { api, ErrorApi } from "@/api/cliente"
import { BadgeEstatus, type Estatus } from "@/componentes/expediente/BadgeEstatus"
import { DialogoAvanzar } from "@/componentes/expediente/DialogoAvanzar"
import { DialogoCerrarExpediente } from "@/componentes/expediente/DialogoCerrarExpediente"
import { LineaDeTiempo, type Transicion } from "@/componentes/expediente/LineaDeTiempo"
import { PanelDocumentos } from "@/componentes/expediente/PanelDocumentos"
import { PanelFinancieroExpediente } from "@/componentes/finanzas/PanelFinancieroExpediente"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

/** Detalle del expediente: cabecera, avance, documentos, rubros y línea de tiempo. */

interface Detalle {
  id: number
  numero: string
  cliente_id: number
  aduana_id: number
  estado: "ABIERTO" | "CERRADO"
  estatus_operativo: Estatus
  fecha_apertura: string
  fecha_cierre: string | null
  cotizacion_id: number | null
  creado_por: number
  creado_en: string
  transiciones: Transicion[]
}

export function ExpedienteDetalle() {
  const { id } = useParams<{ id: string }>()
  const usuario = usarSesion((e) => e.usuario)

  const consulta = useQuery({
    queryKey: ["expediente", Number(id)],
    queryFn: () => api.get<Detalle>(`/api/v1/expedientes/${id}`),
    enabled: Boolean(id),
  })

  if (consulta.isPending) {
    return (
      <p className="py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando expediente…
      </p>
    )
  }

  if (consulta.isError) {
    // 404 cubre dos casos indistinguibles a propósito: no existe, o no es de
    // este cliente. Distinguirlos filtraría la existencia del expediente.
    const esNoEncontrado = consulta.error instanceof ErrorApi && consulta.error.estado === 404
    return (
      <div role="alert" className="py-12 text-center" style={{ color: "var(--destructive)" }}>
        <AlertCircle size={24} aria-hidden="true" className="mx-auto mb-3" />
        <p>{esNoEncontrado ? "Ese expediente no existe" : "No se pudo cargar el expediente"}</p>
        <Link to="/expedientes" className="mt-4 inline-block text-sm underline">
          Volver a la bandeja
        </Link>
      </div>
    )
  }

  const exp = consulta.data

  return (
    <div>
      <Link
        to="/expedientes"
        className="mb-4 inline-flex items-center gap-2 text-sm"
        style={{ color: "var(--fg-muted)" }}
      >
        <ArrowLeft size={16} aria-hidden="true" />
        Expedientes
      </Link>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">{exp.numero}</h1>
        <BadgeEstatus estatus={exp.estatus_operativo} />
        <span className="tabular text-sm" style={{ color: "var(--fg-muted)" }}>
          abierto el {exp.fecha_apertura}
        </span>
      </div>

      {tienePermiso(usuario, "expediente:avanzar") && (
        <div className="mb-8 superficie p-4">
          <DialogoAvanzar expedienteId={exp.id} />
        </div>
      )}

      <h2 className="mb-3 text-sm font-medium uppercase tracking-wide">Documentos</h2>
      <div className="mb-8">
        <PanelDocumentos expedienteId={exp.id} />
      </div>

      {/*
        Los rubros van DESPUÉS de los documentos y ANTES de la línea de tiempo, y ese
        orden es el de la operación: primero se comprueba que el expediente puede
        avanzar, luego lo que cuesta. `rubro:ver` es una compuerta cosmética —el
        servidor exige `requiere_interno` y devuelve 404 al importador antes de mirar
        el permiso— y sirve sólo para no enseñar un panel que va a dar 403.
      */}
      {tienePermiso(usuario, "rubro:ver") && (
        <>
          <h2 className="mb-3 text-sm font-medium uppercase tracking-wide">Rubros</h2>
          <div className="mb-8">
            <PanelFinancieroExpediente expedienteId={exp.id} />
          </div>
        </>
      )}

      {/*
        El cierre va después de los rubros porque es lo que se hace cuando ya no
        queda nada que cargar. Y sólo si el expediente sigue abierto: ofrecer cerrar
        lo que ya está cerrado es ofrecer un 409 con el motivo escrito.

        `cxc:cobrar` y no `expediente:editar`, igual que en el servidor: cerrar
        convierte los servicios en cuentas por cobrar, y §8 le niega al rol
        `operativo` tocar dinero.
      */}
      {exp.estado === "ABIERTO" && tienePermiso(usuario, "cxc:cobrar") && (
        <>
          <h2 className="mb-3 text-sm font-medium uppercase tracking-wide">Cierre</h2>
          <div className="mb-8 superficie p-4">
            <DialogoCerrarExpediente expedienteId={exp.id} />
          </div>
        </>
      )}

      <h2 className="mb-3 text-sm font-medium uppercase tracking-wide">Línea de tiempo</h2>
      <LineaDeTiempo transiciones={exp.transiciones} />
    </div>
  )
}
